// SuperAdmin → Overview tab: platform KPIs, growth area, funnel, at-risk table.
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import EChart from '../charts/EChart';
import { KpiCard, ChartCard, DataTable, EmptyState, ExportButton, exportToCsv, formatDateTime } from './shared';

const ICONS = {
  company: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  user: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  activity: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  pending: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z',
  cost: 'M13 10V3L4 14h7v7l9-11h-7z'
};

const SuperOverview = ({ view, t, period, layout }) => {
  const show = (key) => !layout || layout[key]?.visible !== false;
  const { metrics, previous, activityTrends, funnel: funnelRaw, atRiskCompanies: atRiskRaw, atRiskCompaniesList, pendingCount } = view;

  // Backend may send atRiskCompanies as { expiringSoon, idleOver30d } — prefer the
  // pre-flattened atRiskCompaniesList array, else flatten here. Never pass objects to DataTable.
  const atRiskCompanies = useMemo(() => {
    if (Array.isArray(atRiskCompaniesList) && atRiskCompaniesList.length) return atRiskCompaniesList;
    if (Array.isArray(atRiskRaw)) return atRiskRaw;
    if (atRiskRaw && typeof atRiskRaw === 'object') {
      return [
        ...(atRiskRaw.expiringSoon || []).map((c) => ({
          ...c,
          reason: c.reason || (c.subscriptionEndDate
            ? `Subscription expiring on ${String(c.subscriptionEndDate).slice(0, 10)}`
            : 'Subscription expiring soon'),
          updatedAt: c.updatedAt || c.subscriptionEndDate || c.lastActivity || null
        })),
        ...(atRiskRaw.idleOver30d || []).map((c) => ({
          ...c,
          reason: c.reason || 'No activity for over 30 days',
          updatedAt: c.updatedAt || c.lastActivity || null
        }))
      ];
    }
    return [];
  }, [atRiskRaw, atRiskCompaniesList]);

  // Backend funnel is { byCompanyStatus, bySubscriptionStatus } — map to [{ name, value }] entries.
  const funnel = useMemo(() => {
    if (Array.isArray(funnelRaw)) return funnelRaw;
    if (funnelRaw && typeof funnelRaw === 'object') {
      const out = [];
      Object.entries(funnelRaw).forEach(([group, values]) => {
        if (values && typeof values === 'object' && !Array.isArray(values)) {
          Object.entries(values).forEach(([name, value]) => {
            out.push({ name, value: Number(value) || 0 });
          });
        } else if (typeof values === 'number') {
          out.push({ name: group, value: values });
        }
      });
      return out;
    }
    return [];
  }, [funnelRaw]);

  const growthOption = useMemo(() => {
    const rows = Array.isArray(activityTrends) ? activityTrends : [];
    const hasData = rows.some((r) => (r.login || r.create || r.update || r.delete));
    if (!rows.length || !hasData) return null;
    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      grid: { left: 40, right: 16, top: 24, bottom: 48 },
      xAxis: { type: 'category', boundaryGap: false, data: rows.map((r) => r.name) },
      yAxis: { type: 'value' },
      series: ['login', 'create', 'update', 'delete'].map((k) => ({
        name: k, type: 'line', stack: 'total', smooth: true, showSymbol: false,
        areaStyle: { opacity: 0.35 }, data: rows.map((r) => r[k] || 0)
      }))
    };
  }, [activityTrends]);

  const funnelOption = useMemo(() => {
    const rows = Array.isArray(funnel) ? funnel : [];
    if (!rows.length || !rows.some((r) => r.value > 0)) return null;
    return {
      tooltip: { trigger: 'item' },
      series: [{
        type: 'funnel', left: '10%', width: '80%', label: { show: true },
        data: rows.map((r) => ({ name: r.name, value: r.value }))
      }]
    };
  }, [funnel]);

  const riskColumns = useMemo(() => ([
    { key: 'name', label: t('colCompany') },
    { key: 'reason', label: t('colReason') },
    { key: 'updatedAt', label: t('colDate'), render: (r) => formatDateTime(r.updatedAt || r.lastActivity) }
  ]), [t]);

  return (
    <div className="space-y-4">
      {show('metrics') && (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <KpiCard title={t('kpiCompanies')} value={metrics.totalCompanies} previousValue={previous.totalCompanies}
          icon={ICONS.company} accent="blue" linkText={t('manageCompanies')} linkTo="/superadmin/companies" trendLabel={t('vsPrevious')} />
        <KpiCard title={t('kpiUsers')} value={metrics.totalUsers} previousValue={previous.totalUsers}
          icon={ICONS.user} accent="green" linkText={t('manageUsers')} linkTo="/superadmin/users" trendLabel={t('vsPrevious')} />
        <KpiCard title={t('kpiWau')} value={metrics.weeklyActiveUsers} previousValue={previous.weeklyActiveUsers}
          hint={t('kpiWauHint')} icon={ICONS.activity} accent="purple" trendLabel={t('vsPrevious')} />
        <KpiCard title={t('kpiPending')} value={pendingCount} previousValue={previous.pendingRequests}
          icon={ICONS.pending} accent="amber" linkText={t('manageRequests')} linkTo="/superadmin/subscriptions" trendLabel={t('vsPrevious')} />
        <KpiCard title={t('kpiAiCost')} value={metrics.aiCostTotal ?? '—'} previousValue={previous.aiCostTotal}
          hint={t('kpiAiHint')} icon={ICONS.cost} accent="slate" trendLabel={t('vsPrevious')} />
      </div>
      )}

      {show('growth') && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title={t('growthTitle')} subtitle={t('growthSubtitle', { days: period })} className="lg:col-span-2">
          {growthOption ? <EChart option={growthOption} height={300} /> : <EmptyState message={t('noActivity')} />}
        </ChartCard>
        {show('funnel') && (
        <ChartCard title={t('funnelTitle')} subtitle={t('funnelSubtitle')}>
          {funnelOption ? <EChart option={funnelOption} height={300} /> : <EmptyState message={t('noFunnel')} />}
        </ChartCard>
        )}
      </div>
      )}

      {show('atRisk') && (
      <ChartCard
        title={t('atRiskTitle')}
        subtitle={t('atRiskSubtitle')}
        action={atRiskCompanies.length > 0 ? (
          <ExportButton label={t('exportCsv')} onClick={() => exportToCsv('at-risk-companies.csv', riskColumns, atRiskCompanies)} />
        ) : null}
      >
        <DataTable
          columns={[
            ...riskColumns.slice(0, 2),
            { key: 'link', label: '', render: (r) => (
              <Link to="/superadmin/companies" className="text-blue-600 hover:text-blue-800 font-medium">{t('view')}</Link>
            ) },
            riskColumns[2]
          ]}
          rows={atRiskCompanies}
          emptyMessage={t('noAtRisk')}
        />
      </ChartCard>
      )}
    </div>
  );
};

SuperOverview.propTypes = {
  view: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  period: PropTypes.number.isRequired,
  layout: PropTypes.object
};

export default SuperOverview;
